import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Country {
  id: string;
  name: string;
  isoCode: string;
  active: boolean;
}

interface City {
  id: string;
  name: string;
  active: boolean;
}

interface Project {
  id: string;
  name: string;
  active: boolean;
}

interface PropertyType {
  id: string;
  name: string;
  active: boolean;
}

interface Property {
  id: string;
  code: string;
  street: string;
  floor: number;
  contact: string;
  countryId: string;
  cityId: string;
  projectId: string;
  propertyTypeId: string;
  city?: {
    id: string;
    name: string;
  };
  country?: {
    id: string;
    name: string;
  };
  project?: {
    id: string;
    name: string;
  };
}

interface PropertyEditDialogProps {
  open: boolean;
  property: Property | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PropertyEditDialog: React.FC<PropertyEditDialogProps> = ({
  open,
  property,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    id: '',
    code: '',
    street: '',
    floor: 1,
    contact: '',
    countryId: '',
    cityId: '',
    projectId: '',
    propertyTypeId: ''
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  const isEditing = !!property;

  // Load initial data and populate form when dialog opens
  useEffect(() => {
    if (open && property) {
      console.log('Opening PropertyEditDialog with property:', property);
      console.log('propertyTypeId from property:', property.propertyTypeId);

      fetchInitialData();
      setFormData({
        id: property.id,
        code: property.code,
        street: property.street,
        floor: property.floor,
        contact: property.contact || '',
        countryId: property.countryId || '',
        cityId: property.cityId || '',
        projectId: property.projectId || '',
        propertyTypeId: property.propertyTypeId || ''
      });
      setErrors({});
    }
  }, [open, property]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [countriesRes, citiesRes, projectsRes, propertyTypesRes] = await Promise.all([
        DataService.get('api/Country/all'),
        DataService.get('api/City/all'),
        DataService.get('api/Project/all'),
        DataService.get('api/property-types/all')
      ]);

      // Process countries
      if (countriesRes.ok) {
        const countriesData = await countriesRes.json();
        let countryList: Country[] = [];
        if (countriesData.success && countriesData.data) {
          countryList = countriesData.data.filter((c: Country) => c.active);
        } else if (Array.isArray(countriesData)) {
          countryList = countriesData.filter((c: Country) => c.active);
        }
        setCountries(countryList);
      }

      // Process cities
      if (citiesRes.ok) {
        const citiesData = await citiesRes.json();
        let cityList: City[] = [];
        if (citiesData.success && citiesData.data) {
          cityList = citiesData.data.filter((c: City) => c.active);
        } else if (Array.isArray(citiesData)) {
          cityList = citiesData.filter((c: City) => c.active);
        }
        setCities(cityList);
      }

      // Process projects
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        let projectList: Project[] = [];
        if (projectsData.success && projectsData.data) {
          projectList = projectsData.data.filter((p: Project) => p.active);
        } else if (Array.isArray(projectsData)) {
          projectList = projectsData.filter((p: Project) => p.active);
        }
        setProjects(projectList);
      }

      // Process property types
      if (propertyTypesRes.ok) {
        const propertyTypesData = await propertyTypesRes.json();
        let propertyTypeList: PropertyType[] = [];
        if (propertyTypesData.success && propertyTypesData.data) {
          propertyTypeList = propertyTypesData.data.filter((pt: PropertyType) => pt.active);
        } else if (Array.isArray(propertyTypesData)) {
          propertyTypeList = propertyTypesData.filter((pt: PropertyType) => pt.active);
        }
        console.log('Property types loaded:', propertyTypeList);
        setPropertyTypes(propertyTypeList);
      }

    } catch (error) {
      console.error('Error fetching initial data:', error);
      dispatch(showAlert({
        message: 'Failed to load form data',
        type: 'error'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Property validation
    if (!formData.code.trim()) {
      newErrors.code = 'Property code is required';
    }

    if (!formData.street.trim()) {
      newErrors.street = 'Street address is required';
    }

    if (formData.floor < 0) {
      newErrors.floor = 'Floor must be a positive number';
    }

    if (!formData.countryId) {
      newErrors.countryId = 'Country is required';
    }

    if (!formData.cityId) {
      newErrors.cityId = 'City is required';
    }

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.propertyTypeId) {
      newErrors.propertyTypeId = 'Property type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = {
        id: formData.id,
        code: formData.code.trim(),
        street: formData.street.trim(),
        floor: formData.floor,
        contact: formData.contact.trim(),
        countryId: formData.countryId,
        cityId: formData.cityId,
        projectId: formData.projectId,
        propertyTypeId: formData.propertyTypeId
      };

      console.log('Updating property with data:', payload);

      const response = await DataService.post('api/Property/update', payload);

      if (response.ok) {
        dispatch(showAlert({
          message: 'Property updated successfully!',
          type: 'success'
        }));
        onSuccess();
      } else {
        let errorMessage = 'Failed to update property';
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to update property: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating property:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update property. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  if (!property) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Edit Property
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update property information
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading form data...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Property Information */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon color="primary" />
                  Property Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Property Code"
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value)}
                      error={!!errors.code}
                      helperText={errors.code}
                      disabled={submitting}
                      required
                      placeholder="e.g., P001"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Floor"
                      type="number"
                      value={formData.floor}
                      onChange={(e) => handleInputChange('floor', parseInt(e.target.value) || 0)}
                      error={!!errors.floor}
                      helperText={errors.floor}
                      disabled={submitting}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.street}
                      onChange={(e) => handleInputChange('street', e.target.value)}
                      error={!!errors.street}
                      helperText={errors.street}
                      disabled={submitting}
                      required
                      placeholder="e.g., 123 Main Street"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contact}
                      onChange={(e) => handleInputChange('contact', e.target.value)}
                      error={!!errors.contact}
                      helperText={errors.contact}
                      disabled={submitting}
                      placeholder="Contact person name"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.countryId} required>
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={formData.countryId}
                        label="Country"
                        onChange={(e) => handleInputChange('countryId', e.target.value)}
                        disabled={submitting}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.countryId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.countryId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.cityId} required>
                      <InputLabel>City</InputLabel>
                      <Select
                        value={formData.cityId}
                        label="City"
                        onChange={(e) => handleInputChange('cityId', e.target.value)}
                        disabled={submitting}
                      >
                        {cities.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.cityId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.cityId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.projectId} required>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={formData.projectId}
                        label="Project"
                        onChange={(e) => handleInputChange('projectId', e.target.value)}
                        disabled={submitting}
                      >
                        {projects.map((project) => (
                          <MenuItem key={project.id} value={project.id}>
                            {project.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.projectId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.projectId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.propertyTypeId} required>
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        value={formData.propertyTypeId}
                        label="Property Type"
                        onChange={(e) => handleInputChange('propertyTypeId', e.target.value)}
                        disabled={submitting}
                      >
                        {propertyTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.propertyTypeId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.propertyTypeId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Information Note */}
            <Box
              sx={{
                p: 2,
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderRadius: 1,
                border: '1px solid rgba(25, 118, 210, 0.12)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                Note about Units
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Units cannot be modified during property editing. To manage units, please use the dedicated units management section.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || loading}
          variant="contained"
          size="large"
          startIcon={submitting ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {submitting ? 'Updating...' : 'Update Property'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyEditDialog;